import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { aiService } from "@/lib/ai-service";
import {
	getCheckInAnalysisMaxCheckins,
	getCheckInAnalysisMaxMonths,
} from "@/lib/prompt-config";
import type { AnalysisData } from "../types";
import type { Json } from "@/database.types";

type AnalysisStatus = "idle" | "success" | "error";

interface UseAnalysisGenerationReturn {
	generatingAnalysis: boolean;
	analysisStatus: AnalysisStatus;
	analysisMessage: string;
	generatedAnalysis: AnalysisData | null;
	generateAnalysis: () => Promise<void>;
}

export function useAnalysisGeneration(
	userId: string | undefined,
): UseAnalysisGenerationReturn {
	const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
	const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>("idle");
	const [analysisMessage, setAnalysisMessage] = useState("");
	const [generatedAnalysis, setGeneratedAnalysis] =
		useState<AnalysisData | null>(null);

	const generateAnalysis = useCallback(async () => {
		if (!userId) {
			setAnalysisStatus("error");
			setAnalysisMessage("User not authenticated");
			return;
		}

		setGeneratingAnalysis(true);
		setAnalysisStatus("idle");
		setAnalysisMessage("Generating analysis from your recent check-ins...");

		try {
			const maxMonths = getCheckInAnalysisMaxMonths();
			const cutoff = new Date();
			cutoff.setMonth(cutoff.getMonth() - maxMonths);
			const { data: checkIns, error: fetchError } = await supabase
				.from("daily_checkins")
				.select("id, general_feeling, day_description, created_at")
				.eq("user_id", userId)
				.gte("created_at", cutoff.toISOString())
				.order("created_at", { ascending: false });

			if (fetchError) {
				throw new Error(`Failed to fetch check-ins: ${fetchError.message}`);
			}

			if (!checkIns || checkIns.length === 0) {
				setAnalysisStatus("error");
				setAnalysisMessage(
					`No check-ins found in the last ${maxMonths} months`,
				);
				return;
			}

			setAnalysisMessage(
				`Your check-in was saved. Analyzing check-ins, you can navigate away or wait to see the results.`,
			);

			const validatedCheckIns = checkIns.map((checkIn, index) => {
				if (!checkIn.id || !checkIn.created_at) {
					throw new Error(
						`Check-in ${index} is missing required fields (id or created_at)`,
					);
				}

				if (typeof checkIn.general_feeling !== "number") {
					throw new Error(
						`Check-in ${index} has invalid general_feeling: ${checkIn.general_feeling}`,
					);
				}

				return {
					id: checkIn.id,
					general_feeling: checkIn.general_feeling,
					day_description: checkIn.day_description || "",
					created_at: checkIn.created_at,
				};
			});

			const maxCheckins = getCheckInAnalysisMaxCheckins();
			const limitedCheckIns = [...validatedCheckIns]
				.filter((ci) => new Date(ci.created_at).getTime() >= cutoff.getTime())
				.sort(
					(a, b) =>
						new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
				)
				.slice(0, Math.max(0, maxCheckins));

			const analysis = await aiService.analyzeCheckIns(limitedCheckIns);

			if (!analysis) {
				throw new Error("AI service returned empty analysis");
			}

			// Collect up to three suggestions, deduplicated by simple similarity
			const rawSuggestions: Array<{
				title: string;
				description: string;
				reasoning?: string;
			}> =
				(analysis as any).suggestedReflections ||
				(analysis as any).reflectionSuggestions ||
				(analysis.reflectionSuggestion ? [analysis.reflectionSuggestion] : []);

			const uniqueSuggestions: Array<{
				title: string;
				description: string;
				reasoning?: string;
			}> = [];
			const isSimilar = (a: string, b: string) => {
				const aw = a
					.toLowerCase()
					.split(/\s+/)
					.filter((w) => w.length > 3);
				const bw = b
					.toLowerCase()
					.split(/\s+/)
					.filter((w) => w.length > 3);
				const match = aw.filter((w) =>
					bw.some((x) => x.includes(w) || w.includes(x)),
				).length;
				return match / Math.max(aw.length, 1) > 0.3;
			};
			for (const s of rawSuggestions || []) {
				if (!s?.title || !s?.description) continue;
				if (uniqueSuggestions.some((u) => isSimilar(u.title, s.title)))
					continue;
				uniqueSuggestions.push(s);
				if (uniqueSuggestions.length >= 3) break;
			}

			setAnalysisMessage("Saving analysis to database...");

			const today = new Date().toISOString().split("T")[0];
			const analysisData: AnalysisData = {
				mainTopics: analysis.mainTopics || [],
				negativeTopics: analysis.negativeTopics || [],
				positiveTopics: analysis.positiveTopics || [],
				reflectionSuggestion:
					analysis.reflectionSuggestion || uniqueSuggestions[0] || null,
				reflectionSuggestions:
					uniqueSuggestions.length > 0 ? uniqueSuggestions : undefined,
			};

			const { data: existingAnalysis, error: existingError } = await supabase
				.from("daily_checkin_analysis")
				.select("id")
				.eq("user_id", userId)
				.eq("analysis_date", today)
				.maybeSingle();

			if (existingError) {
				throw new Error(
					`Failed to check existing analysis: ${existingError.message}`,
				);
			}

			const dbPayload = {
				user_id: userId,
				analysis_date: today,
				checkin_count: limitedCheckIns.length,
				last_checkin_date:
					limitedCheckIns.length > 0
						? limitedCheckIns[0].created_at.split("T")[0]
						: today,
				analysis_data: analysisData as unknown as Json,
			};

			if (existingAnalysis) {
				const { error: updateError } = await supabase
					.from("daily_checkin_analysis")
					.update({
						...dbPayload,
						updated_at: new Date().toISOString(),
					})
					.eq("id", existingAnalysis.id);

				if (updateError) {
					throw new Error(`Failed to update analysis: ${updateError.message}`);
				}
			} else {
				const { error: insertError } = await supabase
					.from("daily_checkin_analysis")
					.insert({
						...dbPayload,
						created_at: new Date().toISOString(),
						updated_at: new Date().toISOString(),
					});

				if (insertError) {
					throw new Error(`Failed to insert analysis: ${insertError.message}`);
				}
			}

			setGeneratedAnalysis(analysisData);
			setAnalysisStatus("success");
			setAnalysisMessage(
				"Analysis generated successfully! View it on your dashboard.",
			);
		} catch (error) {
			console.error("❌ Error generating analysis:", error);
			setAnalysisStatus("error");
			setAnalysisMessage(
				error instanceof Error ? error.message : "Failed to generate analysis",
			);
		} finally {
			setGeneratingAnalysis(false);
		}
	}, [userId]);

	return {
		generatingAnalysis,
		analysisStatus,
		analysisMessage,
		generatedAnalysis,
		generateAnalysis,
	};
}
